import { useEffect } from 'react'
import { useAppStore } from '@/store'
import type { UserDirectoryEntry } from '@/utils/userUtils'

/**
 * The organization member directory (name + IdP photo per user id), fetched on
 * demand.
 *
 * Comment surfaces need it to render anyone who is not the current user: the
 * store holds only the signed-in user, so without a directory an author is
 * just a UUID — which is where FS-581's "random character such as `6`" came
 * from. `fetchOrganizationUsers` is otherwise called only by DataCatalog,
 * Settings -> Team and the KG role dialog, so on a board or graph-editor route
 * (where comments actually live) the list is empty unless something asks.
 *
 * Fetches at most once per page load: the store keeps the result, so the
 * length check short-circuits every later mount, and `inFlight` collapses the
 * burst of simultaneous mounts a thread of comments produces into one request.
 */
let inFlight: Promise<void> | null = null

/** Stable identity, so a caller memoising on the directory does not thrash. */
const EMPTY: readonly UserDirectoryEntry[] = Object.freeze([])

export const useOrganizationDirectory = (): readonly UserDirectoryEntry[] => {
  const raw = useAppStore((s) => s.organizationUsers)
  const fetchOrganizationUsers = useAppStore((s) => s.fetchOrganizationUsers)

  // The live store always inits `organizationUsers` to an array and carries the
  // action, but these render inside a tldraw canvas shape: a not-yet-hydrated
  // or partially-mocked store must not throw, because an exception in a comment
  // pin takes the whole board down. Same guard UserMenu carries over
  // `organizations`.
  const directory = Array.isArray(raw) ? (raw as UserDirectoryEntry[]) : EMPTY
  const canFetch = typeof fetchOrganizationUsers === 'function'
  const isPopulated = directory.length > 0

  useEffect(() => {
    if (isPopulated || inFlight || !canFetch) return
    // `passive` because nobody asked for this list: it must not raise an error
    // toast about it, and it must not write `organizationName`, which
    // ShareModal renders. Settings -> Team calls the same action plain, where
    // both ARE the right response (review on #30747).
    //
    // On failure this does not retry WITHIN a mount — `isPopulated` stays false
    // and the deps do not change, so the effect will not re-run. A later mount
    // does try again, which is the behaviour we want: the fallback (a neutral
    // glyph) is correct in the meantime, and nothing loops.
    inFlight = Promise.resolve(fetchOrganizationUsers({ passive: true })).finally(
      () => {
        inFlight = null
      }
    )
  }, [isPopulated, canFetch, fetchOrganizationUsers])

  return directory
}
